import React, { useState } from "react";
import {
  LayoutGrid, FileText, Landmark, Calendar, ListChecks,
  ClipboardList, Users, Bell, Search, ShieldCheck, BarChart3, AlertTriangle,
  Upload, Table2, Mails,
} from "lucide-react";
import { T, STATUS, SERIF, FAMILY, FONT } from "../theme/tokens.js";
import { APP_NAME } from "../config.js";
import { Btn, Label, Wordmark } from "../components/ui/index.jsx";
import { fiscalQuarter, initials, shortName, truncate } from "../lib/format.js";
import { usePresence } from "../lib/useLive.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import ChangePassword from "../components/ChangePassword.jsx";

export const NAV = [
  { key: "overview", label: "Overview", icon: LayoutGrid },
  { key: "contracts", label: "Contracts", icon: FileText },
  { key: "finance", label: "Finance", icon: Landmark },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "dashboards", label: "Dashboards", icon: BarChart3 },
  { key: "risks", label: "Risks", icon: AlertTriangle },
  { key: "actions", label: "Action Items", icon: ListChecks },
  { key: "comms", label: "Communications", icon: Mails },
  { key: "reports", label: "Reports", icon: ClipboardList },
  { key: "trackers", label: "Trackers", icon: Table2 },
  { key: "import", label: "Import", icon: Upload },
  { key: "team", label: "Team", icon: Users },
];

const TITLES = {
  overview: "Overview", contracts: "Contracts", finance: "Finance",
  schedule: "Schedule", dashboards: "Dashboards", risks: "Risk register",
  actions: "Action items", comms: "Communications", reports: "Monthly reports", trackers: "Trackers",
  import: "Import", team: "Team",
};
/* Who else is in this contract right now.
 *
 * The question on a status call is whether the person you are talking to is
 * looking at the same screen you are, and until this the only way to find out
 * was to ask. Initials rather than names, because it sits in a top bar beside
 * a contract switcher and a search box and is not the point of the screen.
 *
 * This is a broadcast between browsers. It touches no table and leaves no
 * record of who was where.
 */
/* The badge itself, given only what it shows. Separated so the visual preview
   can render the shipped component rather than a copy of it that drifts. */
export function PresenceBadge({ live, others = [] }) {
  const names = others.map((o) => o.name).join(", ");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {/* Whether this screen is keeping up by itself. Silence would leave two
          people reading different numbers off the same register and believing
          they were in sync, which is the exact thing this was built to stop. */}
      <span
        title={live
          ? "Changes anyone makes appear here as they happen."
          : "The live connection is not available on this network. This screen asks again every 30 seconds instead."}
        style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11,
                 fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                 color: live ? STATUS.complete.ink : STATUS.inProgress.ink }}
      >
        <span style={{
          width: 7, height: 7, borderRadius: "50%",
          background: live ? STATUS.complete.fill : STATUS.inProgress.fill,
        }} />
        {live ? "Live" : "Catching up"}
      </span>

      {others.length > 0 && (
        <div
          title={`${names} ${others.length === 1 ? "is" : "are"} in this contract now`}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
            textTransform: "uppercase", color: T.soft,
          }}>Also here</span>
          <div style={{ display: "flex" }}>
            {others.slice(0, 4).map((o, i) => (
              <span key={o.id} title={o.name} style={{
                width: 26, height: 26, borderRadius: "50%", background: T.steel, color: "#fff",
                fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center",
                justifyContent: "center", border: `2px solid ${T.bg}`,
                marginLeft: i ? -8 : 0,
              }}>{initials(o.name)}</span>
            ))}
            {others.length > 4 && (
              <span style={{ fontSize: 11.5, color: T.soft, marginLeft: 6, alignSelf: "center" }}>
                +{others.length - 4}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WhoElseIsHere({ contractId }) {
  /* Read defensively. This is an ornament in a top bar, and the visual preview
     renders the shell with no auth provider above it. Nothing here is allowed
     to be the reason a screen fails to draw. */
  const auth = useAuth();
  const user = auth?.user ?? null;
  const me = user ? { id: user.id, name: auth?.profile?.name || user.email } : null;
  const { others, live } = usePresence(contractId ?? "all", me);

  if (!me) return null;
  return <PresenceBadge live={live} others={others} />;
}


export default function AppShell({
  view, onView, profile, contracts, activeContract, isAdmin, canEdit = true,
  onSwitchContext, onSignOut, alerts = 0, query, onQuery, children,
}) {
  const [ctxOpen, setCtxOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const fyq = fiscalQuarter();

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: FAMILY, color: T.ink, display: "flex" }}>
      <style>{FONT}</style>

      <aside style={{
        /* 240 rather than 210 so the logo can be shown at a size where the
           Ambipar line under it is actually readable. That line is a fixed
           fraction of the lockup, so the only way to make it legible is to
           make the whole mark wider, and the rail is what caps the width. */
        width: 240, minWidth: 240, background: T.navy, color: "#fff",
        display: "flex", flexDirection: "column", position: "sticky", top: 0, height: "100vh",
      }}>
        <div style={{ padding: "16px 14px 14px", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
          <div style={{ marginBottom: 10 }}><Wordmark /></div>
          {/* One word, on one line. Broken across two it reads as two names,
              and the sidebar is wide enough to hold it at this tracking. */}
          <div style={{
            fontSize: 10.5, fontWeight: 700, letterSpacing: "0.14em",
            textTransform: "uppercase", color: T.gold, lineHeight: 1.5,
            whiteSpace: "nowrap",
          }}>{APP_NAME}</div>
          <ShieldCheck size={16} color={T.gold} style={{ marginTop: 7 }} />
        </div>

        <nav style={{ padding: "10px 8px", display: "grid", gap: 2, alignContent: "start", flex: 1 }}>
          {/* Import writes into the registers, so it is not a page a viewer
              has any use for. Everything else on this list is reading. */}
          {NAV.filter(({ key }) => canEdit || key !== "import").map(({ key, label, icon: Icon }) => {
            const on = view === key;
            return (
              <button
                key={key}
                onClick={() => onView(key)}
                style={{
                  display: "flex", alignItems: "center", gap: 9, textAlign: "left",
                  background: on ? "rgba(255,255,255,0.12)" : "none", border: "none",
                  borderLeft: on ? `3px solid ${T.gold}` : "3px solid transparent",
                  color: on ? "#fff" : T.sideText, padding: "9px 12px",
                  fontSize: 13.5, fontWeight: on ? 700 : 500, cursor: "pointer",
                }}
              >
                <Icon size={15} />{label}
              </button>
            );
          })}
        </nav>

        <div style={{
          padding: "14px 18px", fontSize: 11.5, color: T.sideText,
          borderTop: "1px solid rgba(255,255,255,0.12)", lineHeight: 1.55,
        }}>
          Controlled operational workspace<br />{fyq.split(" ")[0]} · Internal use
        </div>
      </aside>

      <main style={{ flex: 1, minWidth: 0, padding: "22px 24px 60px", maxWidth: 1220 }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 18, flexWrap: "wrap", gap: 10,
        }}>
          <div>
            <Label>Operations / Executive Reporting</Label>
            <h1 style={{ margin: "2px 0 0", fontSize: 25, color: T.navy, fontFamily: SERIF }}>
              {TITLES[view]}
            </h1>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <WhoElseIsHere contractId={activeContract?.id ?? "all"} />
            {contracts.length > 0 && (
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setCtxOpen(!ctxOpen)}
                  style={{
                    display: "flex", alignItems: "center", gap: 7, background: T.navy,
                    color: "#fff", border: "none", borderRadius: 4, padding: "8px 13px",
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                  }}
                >
                  {activeContract ? truncate(activeContract.name, 26) : "All contracts"}
                  <span style={{ fontSize: 9 }}>▾</span>
                </button>
                {ctxOpen && (
                  <div style={{
                    position: "absolute", left: 0, top: 38, background: T.panel,
                    border: `1px solid ${T.line}`, borderRadius: 4, minWidth: 230,
                    zIndex: 40, boxShadow: "0 4px 14px rgba(20,40,75,0.14)",
                  }}>
                    {contracts.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { onSwitchContext(c.id); setCtxOpen(false); }}
                        style={{
                          display: "block", width: "100%", textAlign: "left", padding: "9px 12px",
                          background: activeContract?.id === c.id ? T.chip : "none",
                          border: "none", fontSize: 13, fontWeight: 600, color: T.navy, cursor: "pointer",
                        }}
                      >{c.name}</button>
                    ))}
                    {isAdmin && (
                      <button
                        onClick={() => { onSwitchContext("all"); setCtxOpen(false); }}
                        style={{
                          display: "block", width: "100%", textAlign: "left", padding: "9px 12px",
                          background: !activeContract ? T.chip : "none", border: "none",
                          borderTop: `1px solid ${T.line}`, fontSize: 13, fontWeight: 700,
                          color: T.gold, cursor: "pointer",
                        }}
                      >All contracts (portfolio)</button>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{
              display: "flex", alignItems: "center", gap: 6, background: T.panel,
              border: `1px solid ${T.line}`, borderRadius: 4, padding: "7px 10px",
            }}>
              <Search size={14} color={T.soft} />
              <input
                value={query}
                onChange={(e) => onQuery(e.target.value)}
                placeholder="Search operational records"
                style={{ border: "none", outline: "none", background: "transparent", fontSize: 13, width: 165, color: T.ink }}
              />
            </div>

            <button
              onClick={() => onView("overview")}
              title={`${alerts} overdue items`}
              style={{ position: "relative", background: "none", border: "none", cursor: "pointer", padding: 4 }}
            >
              <Bell size={18} color={T.navy} />
              {alerts > 0 && (
                <span style={{
                  position: "absolute", top: 2, right: 1, width: 7, height: 7,
                  background: T.gold, borderRadius: "50%",
                }} />
              )}
            </button>

            <div style={{ position: "relative" }}>
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                style={{ display: "flex", alignItems: "center", gap: 7, background: "none", border: "none", cursor: "pointer", padding: 2 }}
              >
                <span style={{
                  width: 26, height: 26, background: T.navy, color: "#fff", fontSize: 11,
                  fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 3,
                }}>{initials(profile?.full_name || profile?.email)}</span>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: T.navy }}>
                  {shortName(profile?.full_name) || profile?.email}
                </span>
                <span style={{ color: T.soft, fontSize: 10 }}>▾</span>
              </button>
              {menuOpen && (
                <div style={{
                  position: "absolute", right: 0, top: 34, background: T.panel,
                  border: `1px solid ${T.line}`, borderRadius: 4, minWidth: 165,
                  zIndex: 40, boxShadow: "0 4px 14px rgba(20,40,75,0.14)",
                }}>
                  {[
                    ["Switch contract", () => onSwitchContext(null)],
                    ["Team settings", () => onView("team")],
                    ["Change password", () => setChanging(true)],
                    ["Sign out", onSignOut],
                  ].map(([label, fn]) => (
                    <button
                      key={label}
                      onClick={() => { setMenuOpen(false); fn(); }}
                      style={{
                        display: "block", width: "100%", textAlign: "left", padding: "9px 12px",
                        background: "none", border: "none", fontSize: 13, color: T.ink, cursor: "pointer",
                      }}
                    >{label}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {changing && <ChangePassword onClose={() => setChanging(false)} />}

        {children}
      </main>
    </div>
  );
}
